<template>
  <div class="agency-profile-container">
    <!-- Enhanced Header Section (Scrollable) -->
    <div class="profile-header-section">
      <!-- Premium Background Header with Glassmorphism -->
      <div
        class="profile-header"
        :class="{ 'edit-mode': agency && profile && agency._id == profile._id }"
      >
        <div class="header-backdrop">
          
          <v-img
            :src="headerImage"
            class="header-background"
            :aspect-ratio="$vuetify.breakpoint.smAndDown ? 414 / 200 : 1654 / 500"
            gradient="to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.7)"
          >
            <!-- Animated Overlay Pattern -->
            <div class="header-overlay">
              <div class="floating-particles">
                <div class="particle" v-for="n in 8" :key="n" :style="getParticleStyle(n)"></div>
              </div>
            </div>

            <!-- Enhanced Edit Button -->
            <v-fade-transition>
              <v-btn
                v-if="agency && agency._id == profile._id"
                class="edit-bg-btn premium-btn"
                fab
                small
                elevation="8"
                @click="onChangeBackground"
              >
                <v-icon class="edit-icon">mdi-camera-enhance</v-icon>
              </v-btn>
            </v-fade-transition>
          </v-img>
        </div>

        <!-- Enhanced Avatar Section with Status Ring -->
        <div class="avatar-container">
          <div v-if="!agency" class="skeleton-avatar">
            <div class="skeleton-avatar-image premium-skeleton">
              <div class="skeleton-pulse"></div>
            </div>
          </div>
          
          <div v-if="agency" class="avatar-wrapper" @click="onEditTitle">
            <!-- Status Ring Animation -->
            <div class="status-ring" :class="{ 'active': isOnline }">
              <div class="ring-pulse"></div>
            </div>
            
            <!-- Show avatar as static image with edit overlay -->
            <div class="profile-avatar premium-avatar" :style="avatarStyle">
              <v-img
                :src="getProfileImage"
                :alt="agency.name"
                class="avatar-image"
                :aspect-ratio="1"
                cover
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              
              <!-- Edit Overlay - Only show for own profile -->
              <div 
                v-if="agency && agency._id == profile._id" 
                class="avatar-edit-overlay"
              >
                <v-icon class="edit-avatar-icon" color="white">mdi-pencil-circle</v-icon>
                <span class="edit-avatar-text">Edit Photo</span>
              </div>
            </div>

            <!-- Verified Badge -->
            <div class="verified-badge" v-if="agency && agency.email_verified">
              <v-icon color="white" size="16">mdi-check-decagram</v-icon>
            </div>
          </div>

          <!-- Enhanced Warning Tooltip -->
          <v-tooltip
            bottom
            v-if="getCompletedInfo && agency && agency._id && profile && profile._id"
            content-class="premium-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="warning-badge" v-bind="attrs" v-on="on">
                <v-icon color="white" size="18">mdi-alert-circle</v-icon>
                <div class="warning-pulse"></div>
              </div>
            </template>
            <div class="tooltip-content">
              <h4>Profile Incomplete</h4>
              <p>Missing: {{ getCompletedInfo }}</p>
            </div>
          </v-tooltip>
        </div>

        <!-- Premium Stats & Actions -->
        <div class="stats-actions-container">
          <!-- Animated Stats Cards -->
          <div class="stats-grid">
            <div class="stat-card" @click="showFollowers">
              <div class="stat-number">
                {{
                  (agency && profile && agency._id === profile._id)
                    ? (profile.followed_by ? profile.followed_by.length : 0)
                    : (agency && agency.followed_by ? agency.followed_by.length : 0)
                }}
              </div>
              <div class="stat-label">Followers</div>
              <div class="stat-trend">
                <v-icon small color="success">mdi-trending-up</v-icon>
              </div>
            </div>
            
            <div class="stat-card" @click="showFollowing">
              <div class="stat-number">
                {{
                  (agency && profile && agency._id === profile._id)
                    ? (profile.followers ? profile.followers.length : 0)
                    : (agency && agency.followers ? agency.followers.length : 0)
                }}
              </div>
              <div class="stat-label">Following</div>
              <div class="stat-trend">
                <v-icon small color="primary">mdi-heart</v-icon>
              </div>
            </div>
          </div>

          <!-- Enhanced Action Buttons -->
          <div class="action-buttons">
            <v-btn
              v-if="agency && profile && agency._id != profile._id && profileType != 'Admin'"
              @click="becameFan"
              :loading="becomingFan"
              :color="getFanned ? 'success' : 'primary'"
              class="premium-action-btn follow-btn"
              :class="{ 'following': getFanned }"
              elevation="4"
              large
            >
              <v-icon left class="btn-icon">{{
                getFanned ? "mdi-account-check" : "mdi-account-plus"
              }}</v-icon>
              <span class="btn-text">{{ getFanned ? "Following" : "Follow" }}</span>
              <div class="btn-ripple"></div>
            </v-btn>

            <v-btn
              v-if="agency && agency._id != profile._id && profileType != 'Admin'"
              @click="goToMessage"
              color="primary"
              outlined
              class="premium-action-btn message-btn"
              elevation="2"
              large
            >
              <v-icon left class="btn-icon">mdi-message-text-outline</v-icon>
              <span class="btn-text">Message</span>
              <div class="btn-shine"></div>
            </v-btn>

            <v-btn
              v-if="profile && agency && profile.type === 'Agency' && profile._id === agency._id"
              @click="downloadNeedsReport"
              :loading="loading"
              :disabled="loading"
              color="secondary"
              outlined
              class="premium-action-btn report-btn"
              elevation="2"
              large
            >
              <v-icon left class="btn-icon">mdi-download-outline</v-icon>
              <span class="btn-text">Export Report</span>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Premium Navigation Tabs with Glassmorphism -->
      <v-card
        flat
        class="navigation-tabs"
        :class="{ 
          'mobile-tabs': $vuetify.breakpoint.smAndDown
        }"
      >
        <v-tabs
          v-model="selectedPage"
          @change="onSelected"
          background-color="transparent"
          color="primary"
          :centered="$vuetify.breakpoint.smAndDown"
          :show-arrows="$vuetify.breakpoint.mdAndUp"
          class="premium-tabs"
        >
          <v-tab
            v-for="(item, index) in menus"
            :key="item.title"
            class="navigation-tab premium-tab"
            :class="{ 'active-premium-tab': selectedPage === index }"
            ripple
          >
            <div class="tab-content">
              <v-icon class="tab-icon">{{ item.icon }}</v-icon>
              <span class="tab-text">{{ item.title }}</span>
              <div class="tab-indicator"></div>
            </div>
          </v-tab>
        </v-tabs>
      </v-card>
    </div>

    <!-- Enhanced Content -->
    <div class="scrollable-content">
      <v-fade-transition mode="out-in">
        <div class="content-wrapper">
          <router-view class="profile-content" />
        </div>
      </v-fade-transition>
    </div>

    <!-- Premium Dialogs -->
    <agency-profile-edit-background
      :dialog="bgDialog"
      :onClose="closeBGDialog"
    />
    <agency-profile-edit-title
      :dialog="editTitleDialog"
      :onClose="onCloseEditDialog"
    />

    <!-- Followers/Following Dialog -->
    <v-dialog v-model="followDialog" max-width="600px" scrollable>
      <v-card>
        <v-card-title class="dialog-header">
          <h3>{{ dialogTitle }}</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="followDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="dialog-content" style="max-height: 500px;">
          <div v-if="loadingFollowData" class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            ></v-progress-circular>
            <p class="mt-4">Loading...</p>
          </div>
          
          <div v-else-if="followList.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
            <p class="mt-4 grey--text">No {{ dialogType }} yet</p>
          </div>
          
          <v-list v-else two-line>
            <v-list-item
              v-for="agency in followList"
              :key="agency._id"
              @click="goToAgencyProfile(agency)"
              class="user-list-item"
            >
              <v-list-item-avatar size="50">
                <v-img :src="agency.midThumbnail || agency.image || require('@/assets/default.jpg')"></v-img>
              </v-list-item-avatar>
              
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ agency.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ agency.email }}
                </v-list-item-subtitle>
              </v-list-item-content>
              
              <v-list-item-action>
                <v-icon color="primary">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Enhanced Snackbar -->
    <v-snackbar 
      v-model="snackbar" 
      :color="snackbarType" 
      timeout="4000" 
      top 
      right
      class="premium-snackbar"
    >
      <div class="snackbar-content">
        <v-icon left>{{ snackbarIcon }}</v-icon>
        {{ errorMessage }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn 
          text 
          v-bind="attrs" 
          @click="snackbar = false"
          class="snackbar-action"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import AgencyProfileEditTitle from "./edit/AgencyProfileEditTitle.vue";
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import AgencyProfileEditBackground from "./edit/AgencyProfileEditBackground.vue";

export default {
  components: {
    AgencyProfileEditBackground,
    AgencyProfileEditTitle
  },
  data() {
    return {
      errorMessage: null,
      snackbar: false,
      snackbarType: 'error',
      snackbarIcon: 'mdi-alert-circle',
      selectedImage: null,
      selectedPage: 0,
      menus: [
        {
          title: "Events",
          router: "agency-profile-events",
          path: "/events",
          icon: "mdi-calendar-multiple"
        },
        {
          title: "About",
          router: "agency-profile-about",
          path: "/needs",
          icon: "mdi-information-variant"
        },
        {
          title: "Needs",
          router: "agency-profile-needs",
          path: "/needs",
          icon: "mdi-hand-heart-outline"
        },
        {
          title: "Volunteers",
          router: "agency-profile-volunteers",
          path: "/volunteers",
          icon: "mdi-account-group-outline"
        }
      ],
      bgDialog: false,
      becomingFan: false,
      editTitleDialog: false,
      loading: false,
      isOnline: true,
      followerCount: 1247,
      followingCount: 89,
      eventCount: 0,
      particles: [],
      followDialog: false,
      followList: [],
      loadingFollowData: false,
      dialogType: "",
      dialogTitle: ""
    };
  },
  computed: {
    ...mapGetters({
      profile: "auth/getProfile",
      profileType: "auth/getType",
      agency: "agency/getAgency"
    }),
    headerImage() {
      if (this.profile && this.agency && this.profile._id === this.agency._id) {
        return this.profile.bgImage || require("@/assets/background0.png");
      }
      return this.agency?.bgImage || require("@/assets/background0.png");
    },
    avatarSize() {
      return this.$vuetify.breakpoint.xs ? 140 : 200;
    },
    avatarStyle() {
      const size = this.avatarSize;
      return {
        width: size + 'px',
        height: size + 'px',
        minWidth: size + 'px',
        minHeight: size + 'px'
      };
    },
    getCompletedInfo() {
      if (!this.agency) return "";
      const missingFields = [];

      const fieldsToCheck = [
        { name: "Logo", value: this.agency.image },
        { name: "Who we are", value: this.agency.who_we_are },
        { name: "What we do", value: this.agency.what_we_do },
        {
          name: "Contact Info",
          value: this.agency.contact_person && this.agency.contact_title
        },
        { name: "Phone", value: this.agency.phone },
        { name: "Address", value: this.agency.address },
        { name: "City", value: this.agency.city },
        { name: "State", value: this.agency.state },
        { name: "Zip", value: this.agency.zip }
      ];

      fieldsToCheck.forEach(field => {
        if (!field.value) missingFields.push(field.name);
      });

      return missingFields.join(", ");
    },
    getProfileImage() {
      // If viewing your own profile (agency._id === profile._id)
      if (this.agency && this.profile && this.agency._id === this.profile._id) {
        return this.profile.midThumbnail || this.profile.image || require("@/assets/default.jpg");
      }
      // If viewing another agency's profile
      if (this.agency) {
        return this.agency.midThumbnail || this.agency.image || require("@/assets/default.jpg");
      }
      // Fallback to default
      return require("@/assets/default.jpg");
    },
    getFanned() {
      if (!this.agency || !this.profile) return false;

      if (this.profileType === "Agency") {
        return this.profile.followers?.includes(this.agency._id) || false;
      } else if (["Volunteer", "Company"].includes(this.profileType)) {
        return this.profile.fanned?.includes(this.agency._id) || false;
      }
      return false;
    }
  },
  methods: {
    ...mapActions({
      getAgency: "agency/getAgency",
      followAgency: "agency/followAgency",
      volunteerFollowAgency: "volunteer/followAgency",
      downloadExcel: "need/downloadExcel"
    }),
    
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    },
    
    getParticleStyle(index) {
      return {
        '--delay': `${index * 0.2}s`,
        '--duration': `${3 + Math.random() * 2}s`,
        '--x': `${Math.random() * 100}%`,
        '--y': `${Math.random() * 100}%`,
        '--size': `${2 + Math.random() * 4}px`
      };
    },
    
    async showFollowers() {
      if (!this.agency) return;
      
      this.dialogType = "followers";
      this.dialogTitle = "Followers";
      this.followDialog = true;
      this.loadingFollowData = true;
      this.followList = [];
      
      try {
        const headers = { Authorization: `${localStorage.getItem("user-token")}` };
        const response = await axios.post(
          process.env.VUE_APP_API_URL + "agency/agencyFollowers",
          { _id: this.agency._id },
          { headers }
        );
        
        if (response.data.success) {
          this.followList = response.data.followerAgencies || [];
        } else {
          this.snackbarType = "error";
          this.snackbarIcon = "mdi-alert-circle";
          this.errorMessage = response.data.message || "Failed to load followers";
          this.snackbar = true;
        }
      } catch (error) {
        console.error("Error fetching followers:", error);
        this.snackbarType = "error";
        this.snackbarIcon = "mdi-alert-circle";
        this.errorMessage = "Failed to load followers";
        this.snackbar = true;
      } finally {
        this.loadingFollowData = false;
      }
    },
    
    async showFollowing() {
      if (!this.agency) return;
      
      this.dialogType = "following";
      this.dialogTitle = "Following";
      this.followDialog = true;
      this.loadingFollowData = true;
      this.followList = [];
      
      try {
        const headers = { Authorization: `${localStorage.getItem("user-token")}` };
        const response = await axios.post(
          process.env.VUE_APP_API_URL + "agency/agencyFollowings",
          { _id: this.agency._id },
          { headers }
        );
        
        if (response.data.success) {
          this.followList = response.data.followingAgencies || [];
        } else {
          this.snackbarType = "error";
          this.snackbarIcon = "mdi-alert-circle";
          this.errorMessage = response.data.message || "Failed to load following";
          this.snackbar = true;
        }
      } catch (error) {
        console.error("Error fetching following:", error);
        this.snackbarType = "error";
        this.snackbarIcon = "mdi-alert-circle";
        this.errorMessage = "Failed to load following";
        this.snackbar = true;
      } finally {
        this.loadingFollowData = false;
      }
    },

    goToAgencyProfile(agency) {
      this.followDialog = false;
      this.$router.push({
        name: "agency-profile-events",
        params: { id: agency._id }
      });
    },
    
    showEvents() {
      // Navigate to events tab
      this.selectedPage = 0;
      this.onSelected(0);
    },

    async loadData() {
      this.loading = true;
      try {
        if (this.$route.params.id) {
          await this.getAgency(this.$route.params.id);
        } else {
          this.$router.push({
            name: "agency-profile-details",
            params: { id: this.profile._id }
          });
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.tokenDialog = true;
        } else {
          console.error(error.response?.data || error);
          this.$router.push({ name: "404" });
        }
      } finally {
        this.loading = false;
      }
    },

    onImageSelected(image) {
      this.selectedImage = image;
    },

    onSelected(index) {
      if (index != undefined && this.$route.name !== this.menus[index].router) {
        this.$router.push({ name: this.menus[index].router });
      } else {
        this.$nextTick(() => {
          const currentRouteIndex = this.menus.findIndex(
            menu => menu.router === this.$route.name
          );
          if (currentRouteIndex >= 0) {
            this.selectedPage = currentRouteIndex;
          }
        });
      }
    },

    onChangeBackground() {
      this.bgDialog = true;
    },

    closeBGDialog() {
      this.bgDialog = false;
      if (this.agency) {
        this.agency.bgImage = this.profile.bgImage;
      }
    },

    goToMessage() {
      this.$store.dispatch("chat/joinRoom", {
        user1ID: this.profile._id,
        user1Type: this.profileType,
        user2ID: this.agency._id,
        user2Type: "Agency"
      });

      this.$router.push({
        name: "chat-details",
        query: { _id: this.agency._id, type: "Agency" }
      });
    },

    async downloadNeedsReport() {
      try {
        this.loading = true;
        const payload = {
          agencyId: this.profile._id,
          exportType: "csv"
        };

        const response = await this.downloadExcel(payload);

        if (!response?.data) {
          throw new Error("Invalid response from API");
        }

        const blob = new Blob([response.data], {
          type: response.headers["content-type"]
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${this.profile.name}_Needs_Report.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
        
        // Success notification
        this.snackbarType = 'success';
        this.snackbarIcon = 'mdi-check-circle';
        this.errorMessage = 'Report downloaded successfully!';
        this.snackbar = true;
        
      } catch (error) {
        console.error("Error downloading report:", error);
        this.snackbarType = 'error';
        this.snackbarIcon = 'mdi-alert-circle';
        this.errorMessage = "Failed to download report. Please try again.";
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },

    async becameFan() {
      try {
        this.becomingFan = true;
        const isNowFanned = !this.getFanned;
        const agencyId = this.agency._id;

        if (this.profileType === "Agency") {
          await this.followAgency({ followed: isNowFanned, agency: agencyId });

          // Update local followers/fanned arrays for instant UI feedback
          if (isNowFanned) {
            // Add to followers if not already present
            if (!this.profile.followers.includes(agencyId)) {
              this.profile.followers.push(agencyId);
            }
            if (!this.agency.followed_by.includes(this.profile._id)) {
              this.agency.followed_by.push(this.profile._id);
            }
          } else {
            // Remove from followers
            this.profile.followers = this.profile.followers.filter(id => id !== agencyId);
            this.agency.followed_by = this.agency.followed_by.filter(id => id !== this.profile._id);
          }
        } else if (["Volunteer", "Company"].includes(this.profileType)) {
          await this.volunteerFollowAgency({ fanned: isNowFanned, agency: agencyId });

          // Update local fanned/followed_by arrays for instant UI feedback
          if (isNowFanned) {
            if (!this.profile.fanned.includes(agencyId)) {
              this.profile.fanned.push(agencyId);
            }
            if (!this.agency.followed_by.includes(this.profile._id)) {
              this.agency.followed_by.push(this.profile._id);
            }
          } else {
            this.profile.fanned = this.profile.fanned.filter(id => id !== agencyId);
            this.agency.followed_by = this.agency.followed_by.filter(id => id !== this.profile._id);
          }
        }

        // Success notification
        this.snackbarType = 'success';
        this.snackbarIcon = 'mdi-heart';
        this.errorMessage = isNowFanned ? 'Now following!' : 'Unfollowed successfully';
        this.snackbar = true;

      } catch (error) {
        console.error("Follow error:", error.response?.data || error);
        this.snackbarType = 'error';
        this.snackbarIcon = 'mdi-alert-circle';
        this.errorMessage = "Failed to update follow status";
        this.snackbar = true;
      } finally {
        this.becomingFan = false;
      }
    },

    onEditTitle() {
      // Only open the edit dialog if viewing your own profile
      if (this.profile && this.agency && this.profile._id === this.agency._id) {
        this.editTitleDialog = true;
      }
    },

    onCloseEditDialog() {
      this.editTitleDialog = false;
    }
  },

  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.loadData();
      }
    },
    "$route.name": {
      immediate: true,
      handler() {
        const currentRouteIndex = this.menus.findIndex(
          menu => menu.router === this.$route.name
        );
        if (currentRouteIndex >= 0) {
          this.selectedPage = currentRouteIndex;
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.agency-profile-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.profile-header-section {
  position: static;
  width: 100%;
  margin-bottom: 20px;
}

.scrollable-content {
  flex: 1;
  overflow: visible;
  padding-top: 0;
  position: relative;
}

.agency-profile-container {
  height: auto;
}

.agency-profile-container .profile-header-section {
  position: static !important;
}

.agency-profile-container .scrollable-content {
  overflow: visible !important;
  padding-top: 0;
  max-height: none !important;
}

.agency-profile-container .navigation-tabs {
  position: static;
}

.profile-header {
  position: relative;
  margin-bottom: 20px;
  border-radius: 24px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
}

.header-backdrop {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
}

.header-background {
  width: 100%;
  height: 100%;
  min-height: 300px;
  max-height: 500px;
  object-fit: cover;
  transition: transform 0.6s ease;
  
  &:hover {
    transform: scale(1.02);
  }
}

.header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(103, 126, 234, 0.1) 0%,
    rgba(118, 75, 162, 0.2) 100%
  );
  backdrop-filter: blur(1px);
}

.floating-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: var(--size);
  height: var(--size);
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: float var(--duration) infinite ease-in-out;
  animation-delay: var(--delay);
  left: var(--x);
  top: var(--y);
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 1;
  }
}

.edit-bg-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
  background: linear-gradient(135deg,#3b7ca1,#084468) !important;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  color: #fff;
  
  &:hover {
    transform: scale(1.1) rotate(15deg);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
}

.edit-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.avatar-container {
  position: absolute;
  bottom: 24px;
  left: 40px;
  z-index: 10;
  cursor: pointer;

  @media (max-width: 960px) {
    left: 50%;
    transform: translateX(-50%);
    bottom: 260px;
  }
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.profile-avatar {
  border: 6px solid rgba(255, 255, 255, 0.9);
  box-shadow: 
    0 15px 35px rgba(0, 0, 0, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: scale(1.05) rotate(2deg);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.25),
      0 10px 20px rgba(0, 0, 0, 0.15);
  }
  
  &:hover .avatar-edit-overlay {
    opacity: 1;
  }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Edit Overlay Styles */
.avatar-edit-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
  cursor: pointer;
}

.avatar-edit-overlay:hover {
  opacity: 1;
}

.edit-avatar-icon {
  font-size: 32px !important;
  margin-bottom: 4px;
  animation: pulse 2s infinite;
}

.edit-avatar-text {
  color: white;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.status-ring {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border-radius: 50%;
  border: 3px solid transparent;
  transition: all 0.3s ease;
  
  &.active {
    border-color: #4caf50;
    animation: statusPulse 2s infinite;
  }
}

.ring-pulse {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  border: 2px solid rgba(76, 175, 80, 0.3);
  animation: ringExpand 2s infinite;
}

@keyframes statusPulse {
  0% { border-color: #4caf50; }
  50% { border-color: rgba(76, 175, 80, 0.5); }
  100% { border-color: #4caf50; }
}

@keyframes ringExpand {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.verified-badge {
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #1e88e5 0%, #1976d2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: verifiedGlow 2s infinite alternate;
}

@keyframes verifiedGlow {
  0% { box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3); }
  100% { box-shadow: 0 4px 20px rgba(30, 136, 229, 0.6); }
}

.warning-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #ff5722 0%, #f44336 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
  }
}

.warning-pulse {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  border: 2px solid rgba(255, 87, 34, 0.5);
  animation: warningPulse 1.5s infinite;
}

@keyframes warningPulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

.premium-tooltip {
  background: rgba(0, 0, 0, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.tooltip-content {
  h4 {
    color: #ff5722;
    margin-bottom: 8px;
    font-weight: 600;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    margin: 0;
  }
}

.stats-actions-container {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 5;

  @media (max-width: 960px) {
    position: static;
    flex-direction: column;
    align-items: center;
    margin-top: 2px;
    padding: 20px;
  }
}

.stats-grid {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    justify-content: center;
    margin-bottom: 24px;
  }
}

.stat-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 16px 20px;
  min-width: 80px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.25);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
}

.stat-number {
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-trend {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0.7;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    justify-content: center;
  }
}

.premium-action-btn {
  position: relative;
  overflow: hidden;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  border-radius: 50px;
  padding: 0 24px;
  height: 48px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
}

.follow-btn {
  background: linear-gradient(135deg,#3b7ca1,#084468) !important;
  color: white !important;
  border: none !important;
  
  &.following {
    background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
  }
  
  &:hover {
    background: linear-gradient(135deg,#3b7ca1,#084468) !important;
    
    &.following {
      background: linear-gradient(135deg, #45a049 0%, #3d8b40 100%) !important;
    }
  }
}

.message-btn, .report-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
}

.btn-icon {
  transition: all 0.3s ease;
}

.btn-text {
  transition: all 0.3s ease;
  color: white;
}

.premium-action-btn:hover .btn-icon {
  transform: scale(1.1);
}

.btn-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.premium-action-btn:active .btn-ripple {
  width: 300px;
  height: 300px;
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.premium-action-btn:hover .btn-shine {
  left: 100%;
}

.navigation-tabs {
  margin-top: 0px;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &.mobile-tabs {
    margin-top: 40px;
  }
}

.navigation-tabs {
  transition: all 0.3s ease;
  position: static;
}

.fixed-header {
  position: static;
}

.scrollable-content {
  padding-top: 0;
}

.glassmorphism {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.premium-tabs {
  .v-tabs-bar {
    background: transparent !important;
  }
}

.navigation-tab {
  position: relative;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 20px 28px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  color: rgba(255, 255, 255, 0.8) !important;
  text-transform: none;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white !important;
    transform: translateY(-2px);
  }
}

.tab-content {
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
  z-index: 2;
  margin: 0 96px;
}

@media (max-width: 599px) {
  .tab-text,
  .tab-indicator {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .navigation-tab .tab-content {
    gap: 6px;
  }
}

@media (min-width: 600px) {
  .tab-content {
    margin: 0 32px;
  }
}

@media (min-width: 960px) {
  .tab-content {
    margin: 0 64px;
  }
}

@media (min-width: 1264px) {
  .tab-content {
    margin: 0 96px;
  }
}

.tab-icon {
  transition: all 0.3s ease;
  font-size: 20px !important;
}

.tab-text {
  transition: all 0.3s ease;
  font-weight: 600;
  color: #3b7ca1;
}

.tab-indicator {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: linear-gradient(135deg,#3b7ca1,#084468);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.active-premium-tab {
  color: white !important;
  background: rgba(255, 255, 255, 0.15);
  
  .tab-indicator {
    width: 80%;
  }
  
  .tab-icon {
    transform: scale(1.1);
    color: #084468;
  }
}

.navigation-tab:hover .tab-icon {
  transform: scale(1.1) rotate(5deg);
}

.content-wrapper {
  backdrop-filter: blur(20px);
  border-radius: 20px;
  min-height: 400px;
  padding: 24px;
  margin-bottom: 20px;
}

.profile-content {
  animation: slideUp 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.premium-skeleton {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 25%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.1) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 50%;
  width: 140px;
  height: 140px;
  position: relative;
  overflow: hidden;
}

.skeleton-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: pulse 1.5s infinite alternate;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.premium-snackbar {
  .v-snack__wrapper {
    background: rgba(0, 0, 0, 0.9) !important;
    backdrop-filter: blur(20px);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
}

.snackbar-action {
  border-radius: 50%;
  min-width: 36px !important;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

/* Responsive Design */
@media (max-width: 600px) {
  .agency-profile-container {
    padding: 0 12px;
    height: auto;
  }
  
  .fixed-header {
    margin-bottom: 16px;
  }
  
  .scrollable-content {
    overflow: visible;
    padding-top: 0;
    max-height: none;
  }
  
  .profile-header {
    border-radius: 16px;
  }
  
  .header-background {
    min-height: 200px;
    max-height: 300px;
  }
  
  .edit-bg-btn {
    top: 16px;
    right: 16px;
  }
  
  .premium-avatar {
    border-width: 4px;
  }
  
  .stats-grid {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }
  
  .stat-card {
    flex: 1;
    min-width: 60px;
    padding: 12px 8px;
  }
  
  .stat-number {
    font-size: 1.4rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
  
  .action-buttons {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }
  
  .premium-action-btn {
    width: 100%;
    height: 44px;
    font-size: 0.9rem;
  }
  
  .navigation-tab {
    padding: 16px 12px;
    
    .tab-content {
      gap: 6px;
    }
    
    .tab-icon {
      font-size: 18px !important;
    }
    
    .tab-text {
      font-size: 0.85rem;
    }
  }
  
  .content-wrapper {
    padding: 16px;
    border-radius: 16px;
  }
}

@media (max-width: 400px) {
  .stat-card {
    padding: 8px 4px;
  }
  
  .stat-number {
    font-size: 1.2rem;
  }
  
  .stat-label {
    font-size: 0.7rem;
  }
  
  .navigation-tab {
    padding: 12px 8px;
    
    .tab-text {
      display: none;
    }
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .premium-action-btn {
    border-width: 2px !important;
  }
  
  .stat-card {
    border-width: 2px;
  }
  
  .navigation-tabs {
    border-width: 2px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Print styles */
@media print {
  .floating-particles,
  .btn-ripple,
  .btn-shine,
  .warning-pulse,
  .ring-pulse {
    display: none;
  }
}

/* Followers/Following Dialog Styles */
.dialog-header {
  background: linear-gradient(135deg, #3b7ca1, #084468);
  color: white;
  padding: 20px 24px;

  h3 {
    font-weight: 600;
    margin: 0;
    color: white;
  }

  .v-btn {
    color: white;
  }
}

.dialog-content {
  padding: 0 !important;
}

.user-list-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: rgba(59, 124, 161, 0.08);
  }

  &:last-child {
    border-bottom: none;
  }
}

.v-list-item__title {
  color: #1a1a1a;
}

.v-list-item__subtitle {
  color: #616161;
  font-size: 0.875rem;
}
</style>